import {
  Box,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { getSelectorAttribute, vyTableSelectors } from '@vy/app-selectors';
import React, { useMemo, useState } from 'react';
import { SearchIcon } from '../../../../components/icons';
import { VyDivider } from '../../atoms';
import type { ColumnConfig } from '../../../../hooks/use-grid-column-settings';

type VyTableColumnSettingsProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  columns: ColumnConfig[];
  handleColumnVisibilityChange: (field: string, isVisible: boolean) => void;
  mandatoryColumnsCount?: number;
};

export const VyTableColumnSettings: React.FC<VyTableColumnSettingsProps> = ({
  anchorEl,
  open,
  onClose,
  columns,
  handleColumnVisibilityChange,
  mandatoryColumnsCount = 2,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const mandatoryColumnFields = useMemo(
    () => new Set(columns.slice(0, mandatoryColumnsCount).map((col) => col.field)),
    [columns, mandatoryColumnsCount],
  );

  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) {
      return columns;
    }

    const query = searchQuery.toLowerCase();

    return columns.filter((column) => column.headerName.toLowerCase().includes(query));
  }, [columns, searchQuery]);

  const mandatoryColumns = useMemo(
    () => filteredColumns.filter((column) => mandatoryColumnFields.has(column.field)),
    [filteredColumns, mandatoryColumnFields],
  );

  const optionalColumns = useMemo(
    () => filteredColumns.filter((column) => !mandatoryColumnFields.has(column.field)),
    [filteredColumns, mandatoryColumnFields],
  );

  return (
    <Popover
      {...getSelectorAttribute(vyTableSelectors.columnSettingsPopover)}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            maxWidth: 225,
            width: '100%',
            height: '429px',
            borderRadius: '8px',
            boxShadow: '0px 8px 16px 0px #0C124514',
            border: '1px solid #F0F4F6',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          },
        },
      }}
    >
      <Stack direction="column" sx={{ height: '100%' }}>
        <Box sx={{
          padding: 2
        }}>
          <TextField
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              '& .MuiFilledInput-root': {
                height: '40px',
                backgroundColor: '#F5F6FA',
                borderRadius: '4px',
              },
              '& .css-1ssscdh-MuiInputBase-input-MuiFilledInput-input': {
                paddingBottom: '5px',
              },
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon color={blueGrey[400]} />
                  </InputAdornment>
                ),
              }
            }}
          />
        </Box>

        <VyDivider flexItem />

        <Box sx={{ flex: 1, overflow: 'auto', paddingX: 2, paddingBottom: 2 }}>
          <Stack spacing={1}>
            {mandatoryColumns.map((column) => (
              <FormControlLabel
                key={column.field}
                control={
                  <Checkbox
                    checked={column.isVisible}
                    disabled
                    size="small"
                    sx={{
                      color: '#D6DBE7',
                      '&.Mui-checked.Mui-disabled': {
                        color: '#05489C',
                        opacity: 0.5,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{
                    fontSize: 14
                  }}>
                    {column.headerName}
                  </Typography>
                }
              />
            ))}

            {optionalColumns.map((column) => (
              <FormControlLabel
                key={column.field}
                control={
                  <Checkbox
                    checked={column.isVisible}
                    onChange={(e) => handleColumnVisibilityChange(column.field, e.target.checked)}
                    size="small"
                    sx={{
                      color: '#D6DBE7',
                      '&.Mui-checked': {
                        color: '#05489C',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{
                    fontSize: 14
                  }}>
                    {column.headerName}
                  </Typography>
                }
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Popover>
  );
};
